import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Form, Input, Spin } from 'antd';
import Modal from 'components/Modal';
import Text from 'components/Text';
import styled from 'styled-components';
import Reason from './Reason';
import { IResponseDataBody } from 'services/response';
import ticketListApis from 'features/ticketList/services/apis';
import { IApiDeleteMultipleTicketBody } from 'features/ticketList/services/types/api';
import toast from 'utils/toast';
import { get } from 'lodash';

enum REASONS {
  DOUBLE_CHARGE = 'Double charge',
  MISTAKE = 'Mistake',
  TRAINING = 'Training',
}

export type MultipleClosedTicketsRef = {
  open: (data?: IApiDeleteMultipleTicketBody, successCallback?: () => void | null) => void;
};

type Props = {};
const DeleteMultipleClosedTicketsModal = forwardRef<MultipleClosedTicketsRef, Props>(
  (props, ref) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [reason, setReason] = useState<string>('');
    const [visible, setVisible] = useState(false);
    const successCallbackRef = useRef<(() => void) | null>(null);

    const [data, setData] = useState<IApiDeleteMultipleTicketBody>();

    const handleClose = () => {
      setData({
        billIds: [],
        staffDeleteId: '',
      });
      setVisible(false);
      setReason('');
      form.resetFields();
    };

    const onFinish = async (values: any) => {
      if (!data) return;
      setLoading(true);
      try {
        const body: IApiDeleteMultipleTicketBody = {
          ...data,
          note: values?.reason || '',
        };
        const res: IResponseDataBody<boolean> = await ticketListApis.deletemMultipleTicket(body);
        if (res.data.data) {
          toast.success(`Deleted ${values?.reason} transaction tickets successful!`);
          handleClose();
          successCallbackRef.current?.();
        } else {
          toast.error('Deleted transaction tickets failed');
        }
      } catch (error) {
        const msg = get(error, 'response.data.message');
        toast.error(msg || 'Deleted transaction tickets failed');
      } finally {
        setLoading(false);
      }
    };

    const onClickReason = (reason: REASONS) => {
      setReason(reason);
      form.setFieldValue('reason', reason);
    };

    useImperativeHandle(ref, () => ({
      open: (data?: IApiDeleteMultipleTicketBody, successCallback?: () => void | null) => {
        if (data) {
          setData(data);
        }
        successCallbackRef.current = successCallback || null;
        setVisible(true);
      },
    }));

    return (
      <Modal
        visible={visible}
        modalTitle='DELETE MULTIPLE CLOSED TICKETS'
        showClose
        okTitle={'Accept'}
        noneFooterStyle
        containerPadding={1.5}
        disableOk={!reason.trim() || loading}
        onClose={handleClose}
        onSubmit={() => form.submit()}
        v2
        noneBodyStyle
        width={'900px'}
      >
        <Spin spinning={loading}>
          <WrapperForm
            className='v2'
            form={form}
            validateTrigger={['onSubmit']}
            onFinish={onFinish}
          >
            <Text textAlign='center' variant='CONTENT_2'>
              Please choose or enter the reason <br /> for deleting this ticket
            </Text>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '90%',
                margin: 'auto',
              }}
            >
              <Reason
                checked={reason === REASONS.DOUBLE_CHARGE}
                message={REASONS.DOUBLE_CHARGE}
                onClick={() => onClickReason(REASONS.DOUBLE_CHARGE)}
              />
              <Reason
                checked={reason === REASONS.MISTAKE}
                message={REASONS.MISTAKE}
                onClick={() => onClickReason(REASONS.MISTAKE)}
              />
              <Reason
                checked={reason === REASONS.TRAINING}
                message={REASONS.TRAINING}
                onClick={() => onClickReason(REASONS.TRAINING)}
              />
            </div>
            <Form.Item name={'reason'} rules={[{ required: true }]}>
              <Input.TextArea
                className='TextArea'
                rows={5}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder='Type your reason here'
              />
            </Form.Item>
          </WrapperForm>
        </Spin>
      </Modal>
    );
  }
);
DeleteMultipleClosedTicketsModal.displayName = 'DeleteMultipleClosedTicketsModal';
export default DeleteMultipleClosedTicketsModal;

const WrapperForm = styled(Form)`
  height: 40vh;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &.v2 {
    height: auto;
    .CONTENT_2 {
      color: #64748b;
      text-align: center;
      font-family: Poppins;
      font-size: 24px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
    }

    textarea {
      font-family: Poppins;
      font-size: 20px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
    }
  }
`;
